/**
 * @file TabDataUpload.jsx
 * @description TabDataUpload component for displaying and managing tab data upload.
 * @component TabDataUpload
 * @exports TabDataUpload - Main component for displaying and managing tab data upload.
 */

import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { TextField, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, LinearProgress, TablePagination } from "@mui/material";
import { fetchCustomerDataUpload, fetchCustomerDataUploadChart } from "../../../services/dashboardModule/dashboardServices";
import { SIZES, SORT_ORDERS } from "../../../common/constants/commonConstants";
import { PAYMENT_STRINGS } from "../../../common/constants/customerModule/paymentConstants";
import CustomerDashDataUploadChart from "../../dashboard/customersDash/charts/CustomerDashDataUploadChart";
import TableSortLabel from '@mui/material/TableSortLabel';
import { getComparator, handleSort } from "../../../hooks/useTable";

const CustomerDataUpload = ({ customerguid = null, isDash = false }) => {
	const [data, setData] = useState([]);
	const [search, setSearch] = useState("");

	const [isLoading, setIsLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
	const [orderBy, setOrderBy] = useState('uploaddate');

	useEffect(() => {
		let mounted = true;
		const load = async () => {
			if (!customerguid) {
				if (mounted) setIsLoading(false);
				return;
			}
			try {
				setIsLoading(true);
				const result = await fetchCustomerDataUpload(customerguid);
				if (mounted) setData(result || []);
			} catch (error) {
				console.error("Error fetching customer data upload:", error);
				if (mounted) setData([]);
			} finally {
				if (mounted) setIsLoading(false);
			}
		};
		load();
		return () => { mounted = false; };
	}, [customerguid]);

	const [chartData, setChartData] = useState([]);

	useEffect(() => {
		const fetchChart = async () => {
			try {
				const data = await fetchCustomerDataUploadChart(customerguid);
				setChartData(data || []);
			} catch (error) {
				console.error("Error fetching data upload chart:", error);
			}
		};

		if (isDash && customerguid) {
			fetchChart();
		}
	}, [customerguid, isDash]);

	const dataToDisplay = useMemo(() => {
		const q = (search || "").toLowerCase().trim();
		const filtered = data.filter((row) => {
			if (!q) return true;
			const dateStr = row.uploaddate ? new Date(row.uploaddate).toLocaleDateString('en-US') : "";
			return (
				dateStr.toLowerCase().includes(q) ||
				(row.DPD || "").toString().toLowerCase().includes(q)
			);
		});
		return [...filtered].sort(getComparator(order, orderBy));
	}, [data, search, order, orderBy]);

	const pagedData = useMemo(() => {
		const start = page * rowsPerPage;
		return dataToDisplay.slice(start, start + rowsPerPage);
	}, [dataToDisplay, page, rowsPerPage]);

	const handleRequestSort = (property) => {
		handleSort(order, orderBy, property, setOrder, setOrderBy);
	};

	useEffect(() => { setPage(0); }, [search]);

	return (
		<Container width="xl" disableGutters sx={{ p: 2 }}>
			<Grid container spacing={2} sx={{ mb: 2 }}>
				<Grid size={SIZES.GROW}>
					<TextField
						fullWidth
						size={SIZES.SMALL}
						value={search}
						variant="outlined"
						placeholder="Search by Date or DPD"
						onChange={e => setSearch(e.target.value)}
					/>
				</Grid>
			</Grid>

			{isDash && (
				<CustomerDashDataUploadChart
					rows={chartData}
					title="Data Upload Volume"
					label="Data Uploads"
					isCurrency={false}
				/>
			)}

			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === 'uploaddate'}
									direction={orderBy === 'uploaddate' ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort('uploaddate')}
								>
									Upload Date
								</TableSortLabel>
							</TableCell>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === 'DPD'}
									direction={orderBy === 'DPD' ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort('DPD')}
								>
									DPD
								</TableSortLabel>
							</TableCell>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === 'totalamountdue'}
									direction={orderBy === 'totalamountdue' ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort('totalamountdue')}
								>
									Total Amount Due
								</TableSortLabel>
							</TableCell>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === 'monthlypayment'}
									direction={orderBy === 'monthlypayment' ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort('monthlypayment')}
								>
									Monthly Payment
								</TableSortLabel>
							</TableCell>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === 'vehicleownamt'}
									direction={orderBy === 'vehicleownamt' ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort('vehicleownamt')}
								>
									Vehicle Own Amt
								</TableSortLabel>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell colSpan={5} align="center">
									<LinearProgress />
								</TableCell>
							</TableRow>
						) : data.length > 0 ? (
							pagedData.map((row, index) => (
								<TableRow key={index}>
									<TableCell align="center">{row.uploaddate ? new Date(row.uploaddate).toLocaleDateString('en-US') : "-"}</TableCell>
									<TableCell align="center">{row.DPD || "-"}</TableCell>
									<TableCell align="center">{row.totalamountdue != null ? row.totalamountdue : "-"}</TableCell>
									<TableCell align="center">{row.monthlypayment != null ? row.monthlypayment : "-"}</TableCell>
									<TableCell align="center">{row.vehicleownamt != null ? row.vehicleownamt : "-"}</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={5} align="center">
									No data found
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				page={page}
				rowsPerPage={rowsPerPage}
				count={dataToDisplay.length}
				rowsPerPageOptions={[10, 25, 50]}
				onPageChange={(event, newPage) => setPage(newPage)}
				onRowsPerPageChange={(event) => {
					setPage(0);
					setRowsPerPage(parseInt(event.target.value, 10));
				}}
			/>
		</Container>
	);
};

export default CustomerDataUpload;